import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Mail, Phone, CreditCard, FileText, type LucideProps } from 'lucide-react';
import type { DataRow, ColumnDefinition, Status, Method } from '../types';

export const ICON_SIZE = 'h-4 w-4';

const STATUS_COLORS: Record<Status, { bg: string; text: string; border: string }> = {
  sent: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200' },
  draft: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
  overdue: { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
  paid: { bg: 'bg-sky-100', text: 'text-sky-800', border: 'border-sky-200' },
  pending: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
  approved: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200' },
  rejected: { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
};

const METHOD_ICONS: Record<Method, React.ComponentType<LucideProps>> = {
  email: Mail,
  phone: Phone,
  card: CreditCard,
  paper: FileText,
};

export function renderCell(row: DataRow, column: ColumnDefinition) {
  const value = row[column.key];
  switch (column.type) {
    case 'avatar':
      return (
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={value.src} alt={value.name} />
            <AvatarFallback>{value.fallback}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium text-gray-800">{value.name}</div>
            <div className="text-xs text-gray-500">{value.detail}</div>
          </div>
        </div>
      );
    case 'status': {
      const status = String(value).toLowerCase() as Status;
      const colors = STATUS_COLORS[status] || STATUS_COLORS.draft;
      return (
        <Badge className={cn('capitalize', colors.bg, colors.text, `border ${colors.border}`)}>
          {String(value)}
        </Badge>
      );
    }
    case 'currency':
      return <span>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value))}</span>;
    case 'date':
      return <span>{new Date(value).toLocaleDateString()}</span>;
    case 'method': {
      const Icon = METHOD_ICONS[String(value).toLowerCase() as Method] || FileText;
      return <Icon className={ICON_SIZE} />;
    }
    default:
      return <span>{String(value)}</span>;
  }
}
